import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Download, ArrowRight, CheckCircle2, XCircle, Terminal } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────────────
   Phase 48 · Public Trust / Attestation landing page.
     No auth required. Renders the live compliance badge, per-control
     breakdown, and a download link for the signed PDF attestation.
   ───────────────────────────────────────────────────────────────────────── */
export default function TrustPage() {
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState('');
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    axios.get(`${API}/api/public/compliance-badge`)
      .then((r) => setPayload(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [API]);

  const pct = payload?.score ?? 0;
  const color = pct >= 90 ? 'var(--accent-green)'
              : pct >= 75 ? 'var(--accent-amber)'
              : pct >= 60 ? '#f97316'
              : 'var(--accent-red)';

  const checks = payload?.checks;
  const row = (label, ok, detail) => (
    <tr data-testid={`trust-check-${label.toLowerCase().replace(/\W+/g, '-')}`}>
      <td>{label}</td>
      <td style={{ color: ok ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 700 }}>
        {ok ? <CheckCircle2 size={12} style={{ display: 'inline' }} /> : <XCircle size={12} style={{ display: 'inline' }} />}
        {' '}{ok ? 'PASS' : 'FAIL'}
      </td>
      <td className="label-xs text-muted">{detail}</td>
    </tr>
  );

  return (
    <div className="min-h-screen" data-testid="trust-page"
         style={{ background: 'var(--bg-base)' }}>
      {/* Top nav */}
      <div className="border-b flex items-center justify-between px-4 py-2"
           style={{ borderColor: 'var(--border)', background: '#0f0f0f' }}>
        <div className="flex items-center gap-4">
          <Terminal style={{ color: 'var(--accent-amber)' }} size={18} />
          <span className="font-sans font-bold tracking-tight" style={{ fontSize: 16 }}>APEXFLOW</span>
          <span className="label-xs" style={{ color: 'var(--accent-amber)' }}>TRUST · LIVE ATTESTATION</span>
        </div>
        <Link to="/login" className="btn-sharp btn-primary" data-testid="trust-to-login"
              style={{ padding: '4px 12px', fontSize: 10 }}>
          SIGN IN <ArrowRight size={10} style={{ display: 'inline' }} />
        </Link>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-5">
        {/* Hero */}
        <div className="panel p-6" style={{ borderColor: color }}>
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={22} style={{ color }} />
            <div>
              <div className="font-sans font-bold" style={{ fontSize: 22, color: 'var(--text-primary)' }}>
                APEXFLOW · COMPLIANCE SCORE
              </div>
              <div className="label-xs" style={{ color: 'var(--text-muted)' }}>
                Live attestation refreshed every 60 seconds · signed HMAC-SHA256
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border" style={{ borderColor: 'var(--border)', background: '#0f0f0f' }}>
              <div className="label-xs" style={{ color: 'var(--text-muted)' }}>GRADE</div>
              <div data-testid="trust-grade" style={{
                fontSize: 64, fontWeight: 900, color, lineHeight: 1, letterSpacing: -2,
              }}>
                {payload?.grade || '—'}
              </div>
              <div className="label-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                institutional · SOC2-equivalent
              </div>
            </div>
            <div className="p-4 border" style={{ borderColor: 'var(--border)', background: '#0f0f0f' }}>
              <div className="label-xs" style={{ color: 'var(--text-muted)' }}>SCORE</div>
              <div data-testid="trust-score" style={{
                fontSize: 48, fontWeight: 900, color, lineHeight: 1,
              }}>
                {payload?.score ?? '—'}<span style={{ fontSize: 22, color: 'var(--text-muted)' }}> / 100</span>
              </div>
              {/* Progress bar */}
              <div className="mt-3" style={{
                height: 6, background: 'var(--border)', position: 'relative',
              }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${pct}%`, background: color, transition: 'width 0.6s ease',
                }} />
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">CONTROLS · VERIFIED LIVE</div>
            <a href={`${API}/api/public/compliance-attestation.pdf`}
               target="_blank" rel="noreferrer"
               className="btn-sharp btn-primary"
               data-testid="trust-pdf-download"
               style={{ padding: '4px 12px', fontSize: 10 }}>
              <Download size={10} style={{ display: 'inline' }} /> DOWNLOAD PDF ATTESTATION
            </a>
          </div>
          <div className="p-4">
            {err && <div className="text-down text-xs">{err}</div>}
            {!payload && !err && <div className="label-xs text-muted">loading attestation…</div>}
            {checks && (
              <table className="data-table" data-testid="trust-controls-table">
                <thead><tr><th>CONTROL</th><th>STATUS</th><th>DETAIL</th></tr></thead>
                <tbody>
                  {row('Audit hash-chain integrity (SHA-256)', checks.audit_chain_ok,
                       `${checks.audit_rows_k}k rows verified`)}
                  {row('OFAC SDN sanctions screening', checks.ofac_rows > 0,
                       checks.ofac_rows > 0
                         ? `${checks.ofac_rows.toLocaleString()} rows · ${checks.ofac_age_hours}h old`
                         : 'not ingested')}
                  {row('KYC pipeline (in-house)', checks.kyc_pipeline_ready,
                       'sanctions screen + submissions + tx monitoring')}
                  {row('Live-trading safety gate',
                       !checks.live_mode_enabled || checks.binance_testnet,
                       `live_mode=${String(checks.live_mode_enabled)} · testnet=${String(checks.binance_testnet)}`)}
                  {row('Rate-limit protection', true,
                       'per-route token-bucket, Redis-backed')}
                  {row('OIDC enterprise SSO', true,
                       'Okta / Azure AD / Keycloak / Auth0 (PKCE S256)')}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Embed snippets */}
        <div className="panel">
          <div className="panel-header"><div className="panel-title">EMBED THIS BADGE</div></div>
          <div className="p-4 space-y-2">
            <div className="label-xs" style={{ color: 'var(--text-muted)' }}>
              Partners + RFP reviewers can embed ApexFlow's live compliance score:
            </div>
            <div style={{ padding: '8px 12px', background: '#0f0f0f', border: '1px solid var(--border)', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-amber)', wordBreak: 'break-all' }}>
              &lt;img src="{API}/api/public/compliance-badge?format=svg" alt="ApexFlow Compliance" /&gt;
            </div>
            <img src={`${API}/api/public/compliance-badge?format=svg`}
                 alt="ApexFlow live compliance badge"
                 data-testid="trust-badge-preview"
                 style={{ height: 28 }} />
          </div>
        </div>

        <div className="text-center label-xs" style={{ color: 'var(--text-muted)' }}>
          APEXFLOW ENGINE · {payload ? `signed attestation generated ${new Date(payload.generated_at).toLocaleString()}` : ''}
        </div>
      </div>
    </div>
  );
}
